<template>
    <tailwind-layout title="Properties">

        <div class="bg-white rounded-lg shadow-sm -mx -mt-10 -mb-10 min-h-[calc(100vh-9.5rem)] overflow-hidden flex flex-col">
            <div
                v-if="subscription_grace_period_warning?.show"
                class="flex flex-col gap-4 border-b border-amber-200 bg-amber-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-between"
                role="alert"
            >
                <div class="flex items-start gap-3 min-w-0">
                    <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100">
                        <FontAwesomeIcon
                            :icon="faCircleExclamation"
                            class="h-5 w-5 text-amber-600"
                            aria-hidden="true"
                        />
                    </div>
                    <div class="min-w-0">
                        <p class="text-sm font-semibold text-amber-950">
                            Your subscription has been canceled
                        </p>
                        <p class="mt-1 text-sm leading-relaxed text-amber-900">
                            Billable properties will be archived in
                            <span class="font-semibold tabular-nums">{{ subscription_grace_period_warning.days_remaining }}</span>
                            {{ subscription_grace_period_warning.days_remaining === 1 ? 'day' : 'days' }}
                            unless you restore your subscription.
                        </p>
                    </div>
                </div>
                <a
                    v-if="subscription_grace_period_warning.pricing_url"
                    :href="subscription_grace_period_warning.pricing_url"
                    class="inline-flex shrink-0 items-center justify-center rounded-md bg-rent-risk-blue-400 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 sm:ml-4"
                >
                    Restore subscription
                </a>
            </div>

            <!-- Header Section -->
            <div class="px-6 pt-6">
                <div class="flex flex-col mb-6">
                    <div class="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
                        <div class="min-w-0 flex-1 space-y-4">
                            <div>
                                <h1 class="text-3xl font-semibold leading-tight tracking-tight text-gray-900">
                                    Properties
                                </h1>
                                <p
                                    v-if="hasSharedProperties"
                                    class="mt-2 max-w-2xl text-sm leading-relaxed text-gray-600"
                                >
                                    <template v-if="activeTab === 'shared'">
                                        View-only access to listings another agent assigned to you.
                                    </template>
                                    <template v-else>
                                        Listings you own and manage, including applications, screening, and settings.
                                    </template>
                                </p>
                            </div>

                            <div
                                v-if="hasSharedProperties"
                                class="space-y-4"
                            >
                                <!-- Mobile: tab picker (Tailwind UI style) -->
                                <div class="grid grid-cols-1 sm:hidden">
                                    <select
                                        aria-label="Select property list"
                                        class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-[#2A375E]"
                                        :value="activeTab"
                                        @change="onPropertiesTabSelectChange"
                                    >
                                        <option value="mine">
                                            My properties ({{ properties.length }})
                                        </option>
                                        <option value="shared">
                                            Shared with me ({{ shared_properties.length }})
                                        </option>
                                    </select>
                                    <FontAwesomeIcon
                                        :icon="faChevronDown"
                                        class="pointer-events-none col-start-1 row-start-1 mr-2 h-5 w-5 self-center justify-self-end text-gray-500"
                                        aria-hidden="true"
                                    />
                                </div>

                                <!-- Desktop: underline tabs with icons -->
                                <div class="hidden sm:block">
                                    <div class="border-b border-gray-200">
                                        <nav class="-mb-px flex space-x-8" aria-label="Property lists">
                                            <button
                                                type="button"
                                                class="group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A375E] focus-visible:ring-offset-2"
                                                :class="activeTab === 'mine'
                                                    ? 'border-[#2A375E] text-[#2A375E]'
                                                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'"
                                                :aria-current="activeTab === 'mine' ? 'page' : undefined"
                                                @click="setTab('mine')"
                                            >
                                                <FontAwesomeIcon
                                                    :icon="faBuilding"
                                                    class="-ml-0.5 mr-2 h-5 w-5"
                                                    :class="activeTab === 'mine' ? 'text-[#2A375E]' : 'text-gray-400 group-hover:text-gray-500'"
                                                    aria-hidden="true"
                                                />
                                                <span>My properties</span>
                                                <span
                                                    v-if="properties.length"
                                                    class="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-700"
                                                >
                                                    {{ properties.length }}
                                                </span>
                                            </button>
                                            <button
                                                type="button"
                                                class="group inline-flex items-center border-b-2 px-1 py-4 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A375E] focus-visible:ring-offset-2"
                                                :class="activeTab === 'shared'
                                                    ? 'border-[#2A375E] text-[#2A375E]'
                                                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'"
                                                :aria-current="activeTab === 'shared' ? 'page' : undefined"
                                                @click="setTab('shared')"
                                            >
                                                <FontAwesomeIcon
                                                    :icon="faEye"
                                                    class="-ml-0.5 mr-2 h-5 w-5"
                                                    :class="activeTab === 'shared' ? 'text-[#2A375E]' : 'text-gray-400 group-hover:text-gray-500'"
                                                    aria-hidden="true"
                                                />
                                                <span>Shared with me</span>
                                                <span
                                                    v-if="shared_properties.length"
                                                    class="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-700"
                                                >
                                                    {{ shared_properties.length }}
                                                </span>
                                            </button>
                                        </nav>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button
                            v-if="activeTab === 'mine'"
                            type="button"
                            class="primaryButton shrink-0 self-start sm:mt-1"
                            @click="onAddPropertyClick"
                        >
                            + ADD PROPERTY
                        </button>
                    </div>
                </div>

                <!-- 86bc70q8c: the account-wide auto-ESO toggle moved to Settings. -->
                <div class="mb-4 border-b border-gray-200 pb-4">
                    <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
                        <div
                            class="flex shrink-0 items-center self-start sm:self-center"
                            @click="show_archived_properties()"
                        >
                            <SwitchGroup as="div" class="flex items-center" v-model="form.trashed">
                                <Switch :class="[form.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']">
                                    <span aria-hidden="true" :class="[form.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                </Switch>
                                <SwitchLabel as="span" class="ml-3 text-sm">
                                    <span class="font-medium text-gray-700">Show Archived Properties</span>
                                </SwitchLabel>
                            </SwitchGroup>
                        </div>
                    </div>
                </div>
                
                <div class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
                    <div class="min-w-0 w-full sm:min-w-[12rem] sm:flex-1">
                        <label for="properties-search" class="sr-only">Search by address</label>
                        <input
                            id="properties-search"
                            v-model="form.search"
                            type="search"
                            autocomplete="street-address"
                            placeholder="Search by address, city, or ZIP"
                            class="w-full pl-4 pr-4 py-2 border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-500 bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400"
                        />
                    </div>
                    <div class="flex flex-wrap items-center gap-2 sm:gap-3 sm:ml-auto sm:shrink-0">
                        <select 
                            v-model="form.sort_by" 
                            class="w-full min-w-0 pl-4 pr-10 py-2 sm:w-56 sm:shrink-0 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400"
                        >
                            <option value="newest">NEWEST</option>
                            <option value="oldest">OLDEST</option>
                            <option value="price_low">PRICE: LOW TO HIGH</option>
                            <option value="price_high">PRICE: HIGH TO LOW</option>
                        </select>
                        <select 
                            v-model.number="form.property_structure_type"
                            class="w-full min-w-0 pl-4 pr-10 py-2 sm:w-56 sm:shrink-0 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400"
                        >
                            <option :value="null">PROPERTY TYPE</option>
                            <option 
                                v-for="(label, value) in propertyStructureTypes" 
                                :key="value" 
                                :value="Number(value)"
                            >
                                {{ label }}
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            
            <!-- Content Section -->
            <div class="px-6 py-6 flex-1 flex flex-col">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" :class="{ 'flex-1': showFirstPropertyCta }">
                <div
                    v-if="showFirstPropertyCta"
                    class="col-span-full flex flex-col items-center justify-center gap-4 py-16 text-center"
                    data-test="first-property-cta"
                >
                    <p class="text-sm text-gray-600">You haven't added a property yet.</p>
                    <button
                        type="button"
                        class="primaryButton"
                        @click="onAddPropertyClick"
                    >
                        + Add Property
                    </button>
                </div>
                <p
                    v-else-if="displayedProperties.length === 0"
                    class="col-span-full py-12 text-center text-sm text-gray-500"
                >
                    <template v-if="activeTab === 'shared'">No properties have been shared with you yet.</template>
                    <template v-else>No properties match your filters.</template>
                </p>
                <div 
                    v-for="property in displayedProperties" 
                    :key="property.id"
                    :class="[
                        property.deleted_at 
                            ? 'bg-red-100 hover:bg-red-50 border border-red-200' 
                            : 'bg-white hover:bg-gray-50',
                        'rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow flex flex-col'
                    ]"
                >
                    <inertia-link :href="route('properties.show', property.id)" class="flex min-h-0 flex-1 flex-col">
                        <!-- Property Image -->
                        <div class="relative h-48 bg-gray-200">
                            <img 
                                :src="property.image" 
                                :alt="property.address"
                                class="w-full h-full object-cover"
                            />
                            <div class="absolute top-3 left-3 z-10 flex max-w-[calc(100%-1.5rem)] flex-col items-start gap-1.5">
                                <div
                                    v-if="property.auto_eso_active"
                                    class="flex items-center gap-1.5 rounded-md bg-green-500 px-2 py-1 text-xs font-semibold text-white shadow-md"
                                    title="Maximum risk reduction is automatically applied for new applicants at this property."
                                >
                                    <font-awesome-icon :icon="faShieldCheck" class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                                    <span>Max risk reduction</span>
                                </div>
                                <PropertyTierBadge
                                    v-if="property.tier"
                                    :tier="property.tier"
                                    :deleted-at="property.deleted_at"
                                    variant="hero"
                                />
                            </div>
                            <div
                                v-if="property.access === 'shared_view'"
                                class="absolute top-3 right-3 max-w-[min(12rem,calc(100%-1.5rem))] truncate rounded-md bg-slate-800/90 px-2 py-1 text-center text-xs font-semibold uppercase tracking-wide text-white shadow-md"
                                title="You can browse this listing but cannot change settings or take owner actions."
                            >
                                View only
                            </div>
                            <div
                                v-else-if="hasModuleIndicators(property)"
                                class="absolute top-3 right-3 flex items-center gap-1"
                                @click.stop
                            >
                                <div
                                    v-if="property.module_indicators.screening > 0"
                                    class="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 shadow-md"
                                    :title="screeningIndicatorDescription(property)"
                                >
                                    <font-awesome-icon :icon="faHouseUser" class="h-3 w-3 text-amber-600" aria-hidden="true" />
                                </div>
                                <div
                                    v-if="property.module_indicators.rent_payments"
                                    class="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 shadow-md"
                                    title="Overdue rent payment"
                                >
                                    <font-awesome-icon :icon="faCalendarCheck" class="h-3 w-3 text-red-500" aria-hidden="true" />
                                </div>
                                <div
                                    v-if="property.module_indicators.maintenance > 0"
                                    class="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 shadow-md"
                                    :title="maintenanceIndicatorDescription(property)"
                                >
                                    <font-awesome-icon :icon="faScrewdriverWrench" class="h-3 w-3 text-red-500" aria-hidden="true" />
                                </div>
                            </div>
                        </div>
                        
                        <!-- Property Details -->
                        <div class="p-4 flex flex-col flex-grow">
                            <div class="mb-2 flex flex-wrap items-center gap-2">
                                <div>
                                    <span class="text-xl font-bold text-gray-900">{{ property.price }}</span>
                                    <span class="text-gray-600">/mo</span>
                                </div>
                                <span v-if="!property.deleted_at" class="inline-flex leading-5 font-semibold rounded-full">
                                    <PropertyLifecycleStatusBadge
                                        :lifecycle-status="property.lifecycle_status"
                                        :deleted-at="property.deleted_at"
                                    />
                                </span>
                            </div>
                            <p class="text-sm text-gray-600 mb-3">{{ property.address }}</p>
                            <p
                                v-if="property.access === 'shared_view' && assignedByDisplay(property)"
                                class="-mt-2 mb-3 text-xs text-gray-500"
                            >
                                <span class="font-semibold text-gray-700">Assigned by</span>
                                {{ assignedByDisplay(property) }}
                                <template v-if="ownerAgentEmailForDisplay(property)">
                                    <span class="text-gray-400"> · </span>
                                    <span class="tabular-nums text-gray-500">{{ ownerAgentEmailForDisplay(property) }}</span>
                                </template>
                            </p>
                            
                            <div class="flex items-center justify-between text-xs text-gray-600 mt-auto">
                                <div class="flex items-center gap-3">
                                    <div class="flex items-center gap-1">
                                        <font-awesome-icon :icon="faHome" class="text-gray-400" />
                                        <span>{{ property.beds }} beds</span>
                                    </div>
                                    <div class="flex items-center gap-1">
                                        <font-awesome-icon :icon="faDroplet" class="text-gray-400" />
                                        <span>{{ property.baths }} baths</span>
                                    </div>
                                </div>
                                <div class="flex items-center gap-1" v-if="property.sqft && property.sqft !== 'N/A'">
                                    <font-awesome-icon :icon="faRuler" class="text-gray-400" />
                                    <span>{{ property.sqft }} sq.ft</span>
                                </div>
                            </div>
                        </div>
                    </inertia-link>
                    <div v-if="activeTab === 'mine' && property.application_link" class="border-t border-gray-100 p-3">
                        <button
                            type="button"
                            class="primaryButton flex w-full items-center justify-center gap-2 py-2.5 text-sm"
                            @click.stop.prevent="openScreeningLinkModal(property)"
                        >
                            Start Screening Tenants
                        </button>
                    </div>
                </div>
                </div>
            </div>
        </div>

        <!-- RAC-10005: shared "Start Screening Tenants" link popup. Same
             ShareModal (linkOnly mode) used on the property show page so
             agents see the same UX from both the property card and the
             property show page. -->
        <ShareModal
            :show="screeningLinkModalOpen"
            :share-url="screeningLinkProperty?.application_link ?? ''"
            :share-text="''"
            title="Start Screening Tenants"
            :link-only="true"
            @close="closeScreeningLinkModal"
        />

        <SubscriptionGracePeriodWarningModal
            :warning="subscription_grace_period_warning"
        />
    </tailwind-layout>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faHome, faDroplet, faRuler, faBuilding, faEye, faChevronDown } from '@fortawesome/pro-light-svg-icons'
import { faShieldCheck, faCircleExclamation, faCalendarCheck, faScrewdriverWrench, faHouseUser } from '@fortawesome/pro-light-svg-icons'
import { Switch, SwitchLabel, SwitchGroup } from '@headlessui/vue'
import ShareModal from '@/Pages/V2/Properties/ShareModal.vue'
import PropertyLifecycleStatusBadge from '@/Pages/V2/Properties/Components/PropertyLifecycleStatusBadge.vue'
import PropertyTierBadge from '@/Pages/V2/Properties/Components/PropertyTierBadge.vue'
import SubscriptionGracePeriodWarningModal from '@/Components/SubscriptionGracePeriodWarningModal.vue'
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'

const route = (typeof window !== 'undefined' && window.route) || ((name, ...params) => {
    console.warn('Route function not available')
    return '#'
})

const props = defineProps({
    filters: {
        type: Object,
        default: () => ({})
    },
    properties: {
        type: Array,
        default: () => []
    },
    shared_properties: {
        type: Array,
        default: () => []
    },
    properties_tab: {
        type: String,
        default: 'mine'
    },
    propertyStructureTypes: {
        type: Object,
        default: () => ({})
    },
    protect_tos_accepted_at: { type: Boolean, default: false },
    is_agent_payment_method_added: { type: Boolean, default: false },
    user_payment_method: { type: Object, default: null },
    stripe_data: { type: [Object, Array], default: () => [] },
    can_add_property_allowed: { type: Boolean, default: true },
    property_add_requires_subscription: { type: Boolean, default: false },
    subscription_grace_period_warning: {
        type: Object,
        default: () => ({
            show: false,
            days_remaining: 0,
            grace_period_ends_at: null,
            pricing_url: null,
        }),
    },
    show_first_property_cta: { type: Boolean, default: false },
})

const getSortBy = () => {
    if (props.filters.sort_by && props.filters.sort_direction) {
        if (props.filters.sort_by === 'created_at' && props.filters.sort_direction === 'desc') return 'newest'
        if (props.filters.sort_by === 'created_at' && props.filters.sort_direction === 'asc') return 'oldest'
        if (props.filters.sort_by === 'rent' && props.filters.sort_direction === 'asc') return 'price_low'
        if (props.filters.sort_by === 'rent' && props.filters.sort_direction === 'desc') return 'price_high'
    }
    return 'newest'
}

const form = ref({
    search: props.filters.search ?? '',
    trashed: props.filters.trashed || false,
    sort_by: getSortBy(),
    property_structure_type: props.filters.property_structure_type ? Number(props.filters.property_structure_type) : null
})

const hasSharedProperties = computed(() => props.shared_properties.length > 0)

const activeTab = ref(
    hasSharedProperties.value && props.properties_tab === 'shared'
        ? 'shared'
        : 'mine'
)

const displayedProperties = computed(() =>
    activeTab.value === 'shared' ? props.shared_properties : props.properties
)

const showFirstPropertyCta = computed(() =>
    activeTab.value === 'mine' && props.show_first_property_cta
)

const hasModuleIndicators = (property) => {
    const indicators = property?.module_indicators
    if (!indicators) {
        return false
    }

    return indicators.screening > 0
        || indicators.rent_payments
        || indicators.maintenance > 0
}

const maintenanceIndicatorDescription = (property) => {
    const count = property?.module_indicators?.maintenance ?? 0
    const label = count === 1 ? 'request' : 'requests'

    return `${count} open maintenance ${label}`
}

const screeningIndicatorDescription = (property) => {
    const count = property?.module_indicators?.screening ?? 0
    const label = count === 1 ? 'application' : 'applications'

    return `${count} ${label} awaiting review`
}

// RAC-10005: "Start Screening Tenants" CTA opens the same ShareModal
// (in linkOnly mode) used on the property show page, instead of
// copying the link directly to clipboard.
const screeningLinkModalOpen = ref(false)
const screeningLinkProperty = ref(null)

const assignedByDisplay = (property) => {
    const name = (property?.owner_agent?.name ?? '').trim()
    if (name) {
        return name
    }

    return (property?.owner_agent?.email ?? '').trim()
}

const ownerAgentEmailForDisplay = (property) => {
    const email = (property?.owner_agent?.email ?? '').trim()
    const name = (property?.owner_agent?.name ?? '').trim()
    if (! email || ! name) {
        return ''
    }

    return email
}

const openScreeningLinkModal = (property) => {
    if (!property?.application_link) {
        return
    }
    screeningLinkProperty.value = property
    screeningLinkModalOpen.value = true
}

const closeScreeningLinkModal = () => {
    screeningLinkModalOpen.value = false
    screeningLinkProperty.value = null
}

const onAddPropertyClick = () => {
    // A new property is free for everyone; Pro is bought for the whole account (86bcd9hg3).
    router.visit(route('properties.create'))
}

const show_archived_properties = () => {
    form.value.trashed = !form.value.trashed
}

const getSortParams = (sortValue) => {
    switch (sortValue) {
        case 'newest':
            return { sort_by: 'created_at', sort_direction: 'desc' }
        case 'oldest':
            return { sort_by: 'created_at', sort_direction: 'asc' }
        case 'price_low':
            return { sort_by: 'rent', sort_direction: 'asc' }
        case 'price_high':
            return { sort_by: 'rent', sort_direction: 'desc' }
        default:
            return { sort_by: 'created_at', sort_direction: 'desc' }
    }
}

const visitIndex = () => {
    const sortParams = getSortParams(form.value.sort_by)
    const params = pickBy({
        search: form.value.search?.trim() || undefined,
        trashed: form.value.trashed ? true : undefined,
        property_structure_type: form.value.property_structure_type || undefined,
        tab: activeTab.value === 'shared' ? 'shared' : undefined,
        ...sortParams
    })
    router.get(route('properties.index'), params, { preserveState: true })
}

const setTab = (tab) => {
    const next = tab === 'shared' ? 'shared' : 'mine'
    if (activeTab.value === next) {
        return
    }
    activeTab.value = next
    visitIndex()
}

const onPropertiesTabSelectChange = (event) => {
    const value = event.target?.value
    if (value === 'mine' || value === 'shared') {
        setTab(value)
    }
}

watch(
    () => props.properties_tab,
    (v) => {
        if (! hasSharedProperties.value) {
            activeTab.value = 'mine'
            return
        }
        activeTab.value = v === 'shared' ? 'shared' : 'mine'
    }
)

watch(
    () => props.shared_properties.length,
    (len) => {
        if (len === 0 && activeTab.value === 'shared') {
            activeTab.value = 'mine'
            visitIndex()
        }
    }
)

/**
 * Debounced, not throttled.
 *
 * throttle(150) fired every 150ms *while* the user typed, so one address
 * produced roughly one request per keystroke — a production log showed 26
 * searches from a single person typing one address, the twelve slowest taking
 * 2.0-4.9s each, and PHP-FPM reporting zero idle workers while it happened.
 * debounce waits for the typing to stop instead, so the same address costs one
 * request rather than twenty.
 *
 * 400ms is below the ~500ms that starts to feel laggy and above a fast typist's
 * inter-key gap, so it reads as live search without behaving like one.
 *
 * The non-search filters (tab, structure type, sort) are single discrete
 * actions, and this same watcher covers them; a 400ms wait on a dropdown is
 * imperceptible, so they are deliberately not split onto a separate path.
 */
watch(
    form,
    debounce(() => visitIndex(), 400),
    { deep: true }
)
</script>
